<template>
  <div :id="titleId" class="wdw-dynbox" :class="titleClass">
    <div
      @click="toggleMinimize"
      class="wdw-dynbox-title"
      :style="dynBoxTitleStyle"
      role="heading"
      :aria-level="headingLevel"
    >
      <div v-if="watermark" class="w-100 d-md-none">
        <!-- show watermark in single-line in sm, hide on md and larger (see below for md and larger)-->
        <Watermark :showControls="showControls" />
        <br />
      </div>
      <span class="wdw-dynbox-title-text" v-dompurify-html="title"></span>
      <span class="wdw-dynbox-title-dynamic">
        <slot name="dynamicTitle"></slot>
      </span>
      <div v-if="watermark" class="d-none d-md-inline">
        <!-- hide inline-watermark in sm, display on md and larger-->
        <Watermark :showControls="showControls" />
      </div>
      <span class="wdw-dynbox-controls" v-if="showControls">
        <a data-widget-toggle="1" alt="Minimieren" @keyup.enter="toggleMinimize" tabindex="0" role="link">
          <span v-if="dynboxMinimized" data-title="Ausklappen" data-placement="top">
            <font-awesome-icon icon="chevron-down" aria-hidden="true" />
            <span class="visually-hidden">Ausklappen</span>
          </span>
          <span v-else data-title="Minimieren" data-placement="top">
            <font-awesome-icon icon="chevron-up" aria-hidden="true" />
            <span class="visually-hidden">Minimieren</span>
          </span>
        </a>
      </span>
    </div>
    <div class="wdw-dynbox-content" :class="contentClass" ref="dynboxContent">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { ref, computed, toRefs, watch } from 'vue';
import useColors from '@/composables/general/useColors';
import Watermark from '@/components/general/Watermark.vue';
export default {
  name: 'WdwDynbox',
  components: {
    Watermark,
  },
  props: {
    config: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    minimize: {
      type: Boolean,
      required: false,
      default: false,
    },
    watermark: {
      type: Boolean,
      required: false,
      default: false,
    },
    titlefontsize: {
      type: String,
      required: false,
      default: '1.6rem',
    },
  },
  emits: ['toggled-minimize'],

  setup(props, context) {
    // ref to html element, will have HTMLElem after component is mounted
    const dynboxContent = ref(null);

    // ref for toggling dynbox
    const dynboxMinimized = ref(props.minimize);

    // return config settings as computed vars
    const titleId = computed(() => {
      return props.config?.titleId || '';
    });
    const showControls = computed(() => {
      return props.config?.showControls || false;
    });
    const titleClass = computed(() => {
      let classString = props.config?.titleClass || '';
      if (!props.title) {
        classString += ' no-title';
      }
      if (showControls.value) {
        classString += ' title-toggle-enabled';
      }
      return classString;
    });
    const headingLevel = computed(() => {
      return props.config?.headingLevel || '2';
    });
    const contentClass = computed(() => {
      let classString = props.config?.contentClass || '';
      if (dynboxMinimized.value) {
        classString += ' hide';
      } else {
        classString += ' show';
      }
      return classString;
    });
    // methods
    function toggleMinimize() {
      if (showControls.value) {
        dynboxMinimized.value = !dynboxMinimized.value;
        context.emit('toggled-minimize', dynboxMinimized.value);
      }
    }
    // Computed
    const dynBoxTitleStyle = computed(() => {
      return 'font-size: ' + props.titlefontsize + ';';
    });

    // watch minimize prop for changes
    watch(
      () => props.minimize,
      (newVal, oldoldValTopic) => {
        toggleMinimize();
      }
    );
    return {
      // computed vars
      titleId,
      titleClass,
      headingLevel,
      contentClass,
      showControls,
      // computed
      dynBoxTitleStyle,
      // method
      toggleMinimize,
      // other vars
      dynboxMinimized,
      // HTML ref
      dynboxContent,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/utils/mixins.scss';
.wdw-dynbox {
  @include border-radius(var(--wdw-dynbox-border-radius, var(--wdw-border-radius)));
  @include box-shadow(var(--wdw-dynbox-box-shadow, 0 1px 4px rgba(0, 0, 0, 0.1)));
  background-color: var(--wdw-dynbox-background-color, var(--wdw-background-color));
  margin: 10px 0;
  overflow: visible;
  &.no-title {
    > .wdw-dynbox-title {
      display: none;
    }

    > .wdw-dynbox-content {
      @include border-radius(var(--wdw-dynbox-border-radius, var(--wdw-border-radius)));
    }
  }
  &.title-toggle-enabled {
    > .wdw-dynbox-title {
      cursor: pointer;
    }
  }

  > .wdw-dynbox-title {
    @include border-top-left-radius(var(--wdw-dynbox-border-radius, var(--wdw-border-radius)));
    @include border-top-right-radius(var(--wdw-dynbox-border-radius, var(--wdw-border-radius)));
    background-color: var(--wdw-dynbox-title-background-color, var(--wdw-primary-color));
    border-bottom: 1px solid #e2e2e2;
    color: var(--wdw-dynbox-title-text-color, var(--wdw-primary-contrast-color));
    font-size: 1.6rem;
    font-weight: 300;
    padding: var(--wdw-dynbox-title-padding, 10px);
    position: relative;
    min-height: 50px;
    overflow: visible;

    .wdw-dynbox-title-text,
    .wdw-dynbox-title-dynamic {
      line-height: 30px;
      vertical-align: middle;
    }

    .wdw-dynbox-title-text.with-controls {
      padding-right: 30px;
      display: block;
    }

    .wdw-dynbox-title-dynamic {
      margin-left: 8px;
      font-size: 80%;
    }

    .wdw-dynbox-controls {
      background: rgba(0, 0, 0, 0.028);
      border-left: 1px solid rgba(0, 0, 0, 0.06);
      cursor: pointer;
      display: block;
      height: 100%;
      padding-left: 5px;
      padding-right: 7px;
      padding-top: 7px;
      position: absolute;
      right: 0;
      top: 0;

      > a {
        display: inline-block;
        @include transition(color 0.3s ease-in);
        color: var(--wdw-dynbox-title-text-color, var(--wdw-primary-contrast-color));
        font-size: 1.2rem;
        padding-top: 1px;
        text-align: center;
        width: 26px;

        &:hover {
          opacity: 0.7;
        }
      }
    }
  }

  > .wdw-dynbox-content {
    @include border-bottom-left-radius(var(--wdw-dynbox-border-radius, var(--wdw-border-radius)));
    @include border-bottom-right-radius(var(--wdw-dynbox-border-radius, var(--wdw-border-radius)));
    @include clearfix();
    background-color: var(--wdw-dynbox-content-background-color, var(--wdw-background-color));
    padding: var(--wdw-dynbox-content-padding, 10px);
    color: var(--wdw-dynbox-content-text-color, var(--wdw-text-color));
    overflow: hidden;
    visibility: visible;
    opacity: 1;
    height: auto;

    &.show,
    &.hide {
      @include transition(height 0.4s ease-in-out, opacity 0.3s ease-in-out);
    }

    &.hide {
      height: 0;
      visibility: hidden;
      opacity: 0;
      padding: 0px;
    }

    &.large {
      font-size: 125%;
    }

    &.no-padding {
      padding-left: 0;
      padding-right: 0;
    }
  }
}
</style>
